/**
 * JWT Validation Utilities
 * Handles custom JWT token validation and expiration checking
 */

/**
 * Decode JWT token without verification (client-side only)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
export function decodeJWT(token) {
  if (!token) return null;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired, false if valid
 */
export function isJWTExpired(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true; // No expiration means invalid token
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
}

/**
 * Get JWT from localStorage
 * @returns {string|null} JWT token or null
 */
export function getStoredJWT() {
  return localStorage.getItem('boneforge_jwt');
}

/**
 * Validate stored JWT and return validation result
 * @returns {object} { valid: boolean, expired: boolean, token: string|null }
 */
export function validateStoredJWT() {
  const token = getStoredJWT();
  
  if (!token) {
    return { valid: false, expired: false, token: null };
  }
  
  const expired = isJWTExpired(token);
  
  return {
    valid: !expired,
    expired,
    token
  };
}

/**
 * Clear JWT from localStorage
 */
export function clearJWT() {
  localStorage.removeItem('boneforge_jwt');
  console.log('🔑 JWT cleared from localStorage');
}

/**
 * Get time until JWT expiration in seconds
 * @param {string} token - JWT token
 * @returns {number} Seconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token) {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return 0;
  }
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeRemaining = decoded.exp - currentTime;
  return Math.max(0, timeRemaining);
}
